import React, {useEffect, useState} from "react";
import {Drawer, IconButton, Typography} from "@mui/material";
import {Close, PermIdentity} from "@mui/icons-material";
import {v4 as uuidv4} from "uuid";
import EnterpriseObject from "../../eoec/eo/EnterpriseObject";
// t1, NOT t6: t6's administration reads are administrator-gated, and this drawer opens from an
// Owner name any user can see.
import AxiosFoundationAPI from "../../util/AxiosFoundationAPI";
import {V1PositionIDOCToArray} from "../../util/Payload";
import enterpriseObjectClassFactory from "../../eoec/EnterpriseObjectClassFactory";
import OrganizationHierarchyView from "../Administration/OrganizationHierarchyView";
import {Grey2, Grey6, Grey17} from "../../theme/styles";

/**
 * The organisation a record's owning Position sits in, raised from the bottom of the page.
 *
 * WHY FROM THE BOTTOM. An org chart is wide and shallow -- it grows sideways as an organisation
 * gains departments and only a few levels deep -- so the edge with room to spare is the bottom one.
 * A side drawer would give the widest thing on the page the narrowest space it has.
 *
 * WHY THE LIST STAYS BEHIND IT. The question this answers is "who is that?", asked while reading a
 * list, and the answer is worth comparing across rows. A drawer that covers half the height leaves
 * the rows visible, so the reader keeps their place and can open the next owner without navigating.
 *
 * MOUNTED ONLY WHILE OPEN. OrganizationHierarchyView builds a JointJS paper and measures its
 * container to fit the tree; a hidden container measures zero, and the chart would come up scaled to
 * nothing on first open. Keying the mount on `open` also means a closed drawer costs nothing.
 */
type Props = {
    open: boolean;
    onClose: () => void;
    /** The position whose organisation is being shown. Used for the heading. */
    positionName: string | null;
    /** That position's USID. Its organisation is what the chart is rooted at. */
    positionUSID: string | null;
};

export default function OwnerHierarchyDrawer({open, onClose, positionName, positionUSID}: Props) {
    const [organizationEO, setOrganizationEO] = useState<EnterpriseObject | undefined>(undefined);
    const [problem, setProblem] = useState<string | null>(null);

    // THE CHART NEEDS THE EC QUERIED *AND* POSITIONED. It reads every organisation from the buffer
    // to build the tree, and roots that tree at the EC's CURRENT record -- it was written to sit
    // under a table where somebody picks a row. Handed an EO that had never been queried it drew its
    // empty state, "Select an organization in the table above", which is true and useless in a
    // drawer with no table above it.
    //
    // So the drawer does what the table would have done: load the organisations, find the one this
    // position belongs to, and make it current.
    useEffect(() => {
        if (!open || !positionUSID) return;

        let cancelled = false;
        setProblem(null);
        setOrganizationEO(undefined);

        // Built on open rather than at mount: most readers of a list never ask for the chart.
        const eo = enterpriseObjectClassFactory("Organization") as EnterpriseObject;
        const orgEC = eo.getEnterpriseComponent("Organization");
        orgEC?.setIDOCRequest("v1.Organization-002");
        orgEC?.setIDOCResponse("v1.Organization-002");

        // The position's own organisation, which the grid row does not carry.
        //
        // The ADMIN client, not the REST one. Positions are a t6 endpoint and t6 sits behind
        // REACT_APP_ADMIN_API_URL; the REST client points at REACT_APP_REST_API_URL -- the integrate
        // API, which does not serve t6 paths -- so the request fails and the drawer reports that the
        // chart could not be loaded.
        AxiosFoundationAPI({
            method: "GET",
            url: "t1/v1/positions",
            // THE QUERY GROUP NEEDS Name, Operator AND Type. The IDOC reader dereferences all three
            // (JSONIntegrationDocument.process_query_group) and a group missing them is a 500, not a
            // 400 -- which is what a hand-rolled QBE without them produced. Shape copied from
            // OrganizationHierarchyView's own call to this endpoint rather than invented again.
            params: {QBE: btoa(JSON.stringify({
                PlatformMessage: {
                    "-idoc": "v1.Position-001",
                    "-id": uuidv4(),
                    Position: {
                        _attributes: {limit: 1, sort: null, offset: 0},
                        QueryGroups: {
                            QueryGroup: [{
                                Name: "primary",
                                Operator: "AND",
                                Type: "QueryGroup",
                                Membership: {
                                    Member: [{
                                        Type: "Attribute",
                                        Name: "USID",
                                        Value: positionUSID,
                                        Operator: "EQUAL",
                                    }],
                                },
                            }],
                        },
                    },
                },
            }))},
            headers: {"idoc-response": "v1.Position-001"},
        }).then((response: any) => {
            if (cancelled) return;
            const rows = V1PositionIDOCToArray(response.data)?.Results ?? [];
            // eslint-disable-next-line no-console
            console.log("[OwnerHierarchyDrawer] position lookup returned", rows.length, "row(s)",
                        rows[0] ? {USID: rows[0].USID, OrganizationUSID: rows[0].OrganizationUSID} : null);
            const organizationUSID = rows[0]?.OrganizationUSID;
            if (!organizationUSID) {
                setProblem("This position is not in an organisation.");
                return;
            }

            // EVERY organisation, because the tree needs the whole realm to walk; the ROOT is chosen
            // by pointing the EC at the one this position sits in.
            const onLoaded = () => {
                orgEC?.removeLoadCompletedListener(onLoaded);
                if (cancelled) return;
                const page: Map<string, any>[] = orgEC?.getBuffer()?.get(orgEC?.getPagePointer()) ?? [];

                // ROOT THE TREE AT THE TOP OF THIS POSITION'S TREE, not at its own organisation.
                // buildTree renders the selected organisation and everything BELOW it, so selecting
                // the position's own org showed a department and its two positions -- true, and not
                // the hierarchy the position sits in. Walking up to the organisation with no parent
                // shows the whole company with the position somewhere inside it, which is the
                // question being asked: where does this owner sit?
                //
                // The walk is bounded by the number of organisations. A parent pointing at a missing
                // or already-seen row stops it rather than looping forever, because a cycle here
                // would hang the drawer rather than draw a wrong chart.
                const byUSID = new Map<string, Map<string, any>>();
                for (const row of page) byUSID.set(String(row.get("USID")), row);

                let rootUSID = String(organizationUSID);
                const seen = new Set<string>([rootUSID]);
                for (;;) {
                    const parent = byUSID.get(rootUSID)?.get("ParentUSID");
                    if (!parent) break;
                    const parentUSID = String(parent);
                    if (!byUSID.has(parentUSID) || seen.has(parentUSID)) break;
                    seen.add(parentUSID);
                    rootUSID = parentUSID;
                }

                const index = page.findIndex((row: Map<string, any>) =>
                    String(row.get("USID")) === rootUSID);
                if (index < 0) {
                    setProblem("That organisation could not be found.");
                    return;
                }
                orgEC?.setRecordPointer(index);
                setOrganizationEO(eo);
            };
            orgEC?.addLoadCompletedListener(onLoaded);
            orgEC?.newQuery();
            orgEC?.executeQuery(1, 500);
        }).catch((error: any) => {
            // SAY WHAT WENT WRONG, in the console at least. Swallowing the error left the drawer
            // showing one sentence for every possible cause -- a 403, a 404, a parse failure -- and
            // nothing anywhere to tell them apart.
            // eslint-disable-next-line no-console
            console.error("[OwnerHierarchyDrawer] could not load the chart",
                          {status: error?.response?.status, data: error?.response?.data,
                           message: error?.message, url: error?.config?.url,
                           baseURL: error?.config?.baseURL});
            if (!cancelled) {
                const status = error?.response?.status;
                setProblem(status
                    ? `The organisation chart could not be loaded (${status}).`
                    : "The organisation chart could not be loaded.");
            }
        });

        return () => { cancelled = true; };
    }, [open, positionUSID]);

    // slotProps.paper, not PaperProps -- MUI v9 removed the latter.
    return (
        <Drawer anchor="bottom" open={open} onClose={onClose}
                slotProps={{paper: {sx: {height: "60vh", borderTopLeftRadius: 8,
                                         borderTopRightRadius: 8}}}}>
            <div style={{display: "flex", alignItems: "center", gap: 10, padding: "14px 20px",
                         borderBottom: `1px solid ${Grey2}`}}>
                <PermIdentity style={{fontSize: 20, color: Grey6}}/>
                <Typography style={{fontSize: 16, fontWeight: 500, color: Grey17}}>
                    {positionName || "Organisation"}
                </Typography>
                <Typography style={{fontSize: 13, color: Grey6}}>
                    &mdash; where this position sits
                </Typography>
                <div style={{flexGrow: 1}}/>
                <IconButton onClick={onClose} size="small" aria-label="Close the organisation chart">
                    <Close style={{fontSize: 20, color: Grey6}}/>
                </IconButton>
            </div>

            {/* flexGrow with minHeight:0 so the chart gets the remaining height and can scroll
                inside it, rather than pushing the drawer taller than its own paper. */}
            <div style={{flexGrow: 1, minHeight: 0, display: "flex", flexDirection: "column"}}>
                {problem ? (
                    <Typography style={{padding: 40, textAlign: "center", fontSize: 14, color: Grey6}}>
                        {problem}
                    </Typography>
                ) : open && organizationEO ? (
                    <OrganizationHierarchyView enterpriseObject={organizationEO}
                                              highlightPositionUSID={positionUSID}
                                              captionFor={positionName}/>
                ) : (
                    <Typography style={{padding: 40, textAlign: "center", fontSize: 14, color: Grey6}}>
                        Loading the organisation chart&hellip;
                    </Typography>
                )}
            </div>
        </Drawer>
    );
}
